import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { Card, EmptyState, TabHeading } from "./StudioShared";
import { ISO_DAYS, DAY_KEYS, hhmmToTimeStr, timeStrToHhmm } from "./helpers";

// Un bloc horaire = {days:[iso...], start:"HH:MM", end:"HH:MM", loop_once}
// que l'on convertit vers/depuis la forme backend {start_time, end_time (HHMM int), days, loop_once}.
function itemToBlock(it) {
  return {
    days: Array.isArray(it.days) ? it.days : [],
    start: hhmmToTimeStr(it.start_time),
    end: hhmmToTimeStr(it.end_time),
    loop_once: !!it.loop_once,
  };
}
function blockToItem(b) {
  return {
    days: b.days,
    start_time: timeStrToHhmm(b.start),
    end_time: timeStrToHhmm(b.end),
    loop_once: !!b.loop_once,
  };
}

function ScheduleEditor({ blocks, setBlocks }) {
  const { t } = useTranslation();

  const update = (i, patch) => setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const toggleDay = (i, day) =>
    setBlocks((bs) =>
      bs.map((b, idx) =>
        idx === i
          ? { ...b, days: b.days.includes(day) ? b.days.filter((d) => d !== day) : [...b.days, day].sort((a, c) => a - c) }
          : b
      )
    );
  const addBlock = () => setBlocks((bs) => [...bs, { days: [1, 2, 3, 4, 5], start: "08:00", end: "10:00", loop_once: false }]);
  const removeBlock = (i) => setBlocks((bs) => bs.filter((_, idx) => idx !== i));

  return (
    <div className="mt-4 space-y-4">
      {blocks.length === 0 && <p className="text-sm text-gray-500">{t("scheduler.noBlocks")}</p>}
      {blocks.map((b, i) => (
        <div key={i} className="rounded-xl border border-gray-100 p-4 bg-gray-50/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500">{t("scheduler.days")}</span>
            <button onClick={() => removeBlock(i)} className="text-xs text-red-600 hover:underline">
              {t("scheduler.removeBlock")}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {ISO_DAYS.map((d) => (
              <button
                key={d}
                onClick={() => toggleDay(i, d)}
                className={`text-xs px-2.5 py-1.5 rounded-md border ${
                  b.days.includes(d)
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-purple-300"
                }`}
              >
                {t(`scheduler.day.${DAY_KEYS[d]}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <label className="text-xs text-gray-500">
              <span className="block mb-1">{t("scheduler.start")}</span>
              <input
                type="time"
                value={b.start}
                onChange={(e) => update(i, { start: e.target.value })}
                className="border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-800"
              />
            </label>
            <label className="text-xs text-gray-500">
              <span className="block mb-1">{t("scheduler.end")}</span>
              <input
                type="time"
                value={b.end}
                onChange={(e) => update(i, { end: e.target.value })}
                className="border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-800"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={b.loop_once} onChange={(e) => update(i, { loop_once: e.target.checked })} />
              {t("scheduler.loopOnce")}
            </label>
          </div>
        </div>
      ))}
      <button onClick={addBlock} className="text-sm text-purple-700 hover:underline">
        ＋ {t("scheduler.addBlock")}
      </button>
    </div>
  );
}

function PlaylistCard({ id, playlist, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(playlist.name || "");
  const [enabled, setEnabled] = useState(!!playlist.is_enabled);
  const [blocks, setBlocks] = useState((playlist.schedule_items || []).map(itemToBlock));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const itemsCount = (playlist.schedule_items || []).length;

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put(`/api/radios/${id}/playlists/${playlist.id}`, {
        name,
        is_enabled: enabled,
        schedule_items: blocks.map(blockToItem),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      onChanged();
    } catch { /* ignore */ } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await api.put(`/api/radios/${id}/playlists/${playlist.id}`, { is_enabled: next });
      onChanged();
    } catch {
      setEnabled(!next);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("scheduler.confirmDelete"))) return;
    try {
      await api.delete(`/api/radios/${id}/playlists/${playlist.id}`);
      onChanged();
    } catch { /* ignore */ }
  };

  return (
    <Card>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 truncate">{playlist.name}</span>
            {playlist.type && (
              <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                {playlist.type}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {itemsCount > 0 ? t("scheduler.scheduleSummary", { count: itemsCount }) : t("scheduler.noSchedule")}
          </p>
        </div>

        <button
          onClick={toggleEnabled}
          className={`text-xs px-2.5 py-1.5 rounded-full ${enabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}
        >
          {enabled ? t("scheduler.enabled") : t("scheduler.disabled")}
        </button>
        <button onClick={() => setOpen((o) => !o)} className="text-sm bg-purple-50 text-purple-700 px-3 py-1.5 rounded-md hover:bg-purple-100">
          {t("scheduler.editSchedule")}
        </button>
        <button onClick={remove} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 flex items-center justify-center">
          ✕
        </button>
      </div>

      {open && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <label className="text-xs text-gray-500 block">
            <span className="block mb-1">{t("scheduler.rename")}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full sm:w-72 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800"
            />
          </label>

          <p className="text-xs font-medium text-gray-500 mt-4">{t("scheduler.blocks")}</p>
          <ScheduleEditor blocks={blocks} setBlocks={setBlocks} />

          <div className="flex items-center gap-3 mt-5">
            <button
              onClick={save}
              disabled={saving}
              className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              {t("scheduler.save")}
            </button>
            {saved && <span className="text-sm text-green-600">{t("scheduler.saved")} ✓</span>}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function SchedulerTab({ id }) {
  const { t } = useTranslation();
  const [playlists, setPlaylists] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/radios/${id}/playlists`);
      setPlaylists(Array.isArray(r.data) ? r.data : []);
    } catch {
      setPlaylists([]);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post(`/api/radios/${id}/playlists`, {
        name: newName.trim(),
        type: "default",
        is_enabled: true,
      });
      setNewName("");
      setAdding(false);
      await load();
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <TabHeading
        title={t("scheduler.title")}
        subtitle={t("scheduler.subtitle")}
        action={
          <button onClick={() => setAdding((a) => !a)} className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md">
            ＋ {t("scheduler.add")}
          </button>
        }
      />

      {adding && (
        <Card className="mb-5">
          <div className="flex flex-wrap items-center gap-3">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && create()}
              placeholder={t("scheduler.namePlaceholder")}
              className="flex-1 min-w-[12rem] border border-gray-200 rounded-md px-3 py-2 text-sm"
            />
            <button onClick={create} disabled={creating || !newName.trim()} className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md disabled:opacity-50">
              {t("scheduler.create")}
            </button>
            <button onClick={() => { setAdding(false); setNewName(""); }} className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md">
              {t("studio.actions.cancel")}
            </button>
          </div>
        </Card>
      )}

      {playlists === null ? (
        <p className="text-gray-500">{t("studio.loading")}</p>
      ) : playlists.length === 0 ? (
        <Card>
          <EmptyState icon="🗓️" title={t("scheduler.empty")} hint={t("scheduler.emptyHint")} />
        </Card>
      ) : (
        <div className="space-y-3">
          {playlists.map((p) => (
            <PlaylistCard key={p.id} id={id} playlist={p} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}
